import React, { useEffect, useState } from "react";
import "../style/settings.css";
import {
  FaCog,
  FaSave,
  FaUpload,
  FaFileInvoice,
  FaHashtag,
  FaFileAlt,
  FaSignature,
  FaEdit,
  FaTimes,
} from "react-icons/fa";

const Settings = () => {

  const [settings, setSettings] = useState({
    invoicePrefix: "",
    challanFormat: "",
    challanSequence: "",
    terms: "",
    signature: null,
    defaultFormat: false,
  });

  const [originalSettings, setOriginalSettings] = useState({
    invoicePrefix: "",
    challanFormat: "",
    challanSequence: "",
    terms: "",
    signature: null,
    defaultFormat: false,
  });

  const [signaturePreview, setSignaturePreview] = useState(null);
  const [originalSignaturePreview, setOriginalSignaturePreview] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const token = localStorage.getItem("token");

  /* ================= LOAD SETTINGS ================= */

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {

    try {

      const res = await fetch(
        "https://ctmapi.zerlak.com/api/v1/settings/getSettings",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 204) return;

      const data = await res.json();

      const newSettings = {
        invoicePrefix: data.invoicePrefix || "",
        challanFormat: data.challanNumberFormat || "",
        challanSequence: data.challanSequence || "",
        terms: data.terms || "",
        defaultFormat: data.defaultFormat || false,
        signature: null,
      };

      setSettings(newSettings);
      setOriginalSettings(newSettings);

      if (data.signatureBase64) {
        setSignaturePreview(data.signatureBase64);
        setOriginalSignaturePreview(data.signatureBase64);
      }

    } catch (err) {
      console.error("Failed to load settings", err);
    }

  };

  /* ================= HANDLE INPUT ================= */

  const handleChange = (field, value) => {

    setSettings(prev => ({
      ...prev,
      [field]: value,
    }));

  };

  /* ================= FILE UPLOAD ================= */

  const handleFileUpload = (e) => {

    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please upload image only");
      return;
    }

    setSettings(prev => ({
      ...prev,
      signature: file,
    }));

    const reader = new FileReader();

    reader.onloadend = () => {
      setSignaturePreview(reader.result);
    };

    reader.readAsDataURL(file);

  };

  /* ================= FILE TO BASE64 ================= */

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  /* ================= EDIT MODE FUNCTIONS ================= */

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleSaveChanges = async () => {
    try {
      let signatureBase64 = signaturePreview;

      if (settings.signature) {
        signatureBase64 = await fileToBase64(settings.signature);
      }

      const payload = {
        invoicePrefix: settings.invoicePrefix,
        challanNumberFormat: settings.challanFormat,
        challanSequence: Number(settings.challanSequence),
        terms: settings.terms,
        defaultFormat: settings.defaultFormat,
        signatureBase64: signatureBase64,
      };

      const res = await fetch(
        "https://ctmapi.zerlak.com/api/v1/settings/update",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Update failed");

      setShowSuccess(true);
      setIsEditMode(false);
      
      // Update original settings
      setOriginalSettings({
        ...settings,
        signature: null,
      });
      setOriginalSignaturePreview(signatureBase64);

      setTimeout(() => setShowSuccess(false), 3000);

    } catch (err) {
      console.error(err);
      alert("Failed to update settings");
    }
  };

  const handleCancel = () => {
    setSettings(originalSettings);
    setSignaturePreview(originalSignaturePreview);
    setIsEditMode(false);
  };

  return (
    <div className="settings-page">

      <div className="settings-container">

        {showSuccess && (
          <div className="alert alert-success">
            Settings updated successfully!
          </div>
        )}

        <div className="settings-card">

          <div className="settings-header">

            <div className="settings-header-left">

              <div className="settings-avatar">
                <FaCog />
              </div>

              <div className="settings-details">
                <h2 className="settings-title">System Settings</h2>
              </div>

            </div>

            <div className="settings-header-right">

              {!isEditMode ? (
                <button className="edit-btn" onClick={handleEdit}>
                  <FaEdit />
                  <span>Edit Settings</span>
                </button>
              ) : (
                <>
                  <button className="cancel-btn" onClick={handleCancel}>
                    <FaTimes />
                    <span>Cancel</span>
                  </button>
                  <button className="save-changes-btn" onClick={handleSaveChanges}>
                    <FaSave />
                    <span>Save Changes</span>
                  </button>
                </>
              )}

            </div>

          </div>

          <div className="settings-grid">

            {/* Invoice Prefix */}

            <div className="settings-field">

              <label className="settings-label">
                <FaFileInvoice /> Invoice Prefix
              </label>

              <input
                type="text"
                className="settings-input"
                value={settings.invoicePrefix}
                onChange={(e) =>
                  handleChange("invoicePrefix", e.target.value)
                }
                disabled={!isEditMode}
              />

            </div>

            {/* Challan Format */}

            <div className="settings-field">

              <label className="settings-label">
                <FaHashtag /> Challan Number Format
              </label>

              <div className="input-container">

                <input
                  type="text"
                  className="settings-input"
                  value={settings.challanFormat}
                  onChange={(e) =>
                    handleChange("challanFormat", e.target.value)
                  }
                  disabled={!isEditMode}
                />

                <label className="inline-checkbox">

                  <input
                    type="checkbox"
                    checked={settings.defaultFormat}
                    onChange={(e) =>
                      handleChange("defaultFormat", e.target.checked)
                    }
                    disabled={!isEditMode}
                  />

                  Default

                </label>

              </div>

            </div>

            {/* Challan Sequence */}

            <div className="settings-field">

              <label className="settings-label">
                <FaHashtag /> Challan Sequence
              </label>

              <input
                type="number"
                className="settings-input"
                value={settings.challanSequence}
                onChange={(e) =>
                  handleChange("challanSequence", e.target.value)
                }
                disabled={!isEditMode}
              />

            </div>

            {/* Terms */}

            <div className="settings-field">

              <label className="settings-label">
                <FaFileAlt /> Terms & Conditions
              </label>

              <textarea
                className="settings-input textarea"
                value={settings.terms}
                onChange={(e) =>
                  handleChange("terms", e.target.value)
                }
                disabled={!isEditMode}
              />

            </div>

            {/* Signature */}

            <div className="settings-field signature-field">

              <label className="settings-label">
                <FaSignature /> Authorized Signature
              </label>

              <div
                className={`signature-upload-box ${!isEditMode ? 'disabled' : ''}`}
                onClick={() => {
                  if (isEditMode) {
                    document.getElementById("signatureInput").click();
                  }
                }}
              >

                {signaturePreview ? (
                  <img
                    src={signaturePreview}
                    alt="Signature"
                    className="signature-image"
                  />
                ) : (
                  <div className="signature-placeholder">
                    <FaUpload />
                    <span>Upload Signature</span>
                  </div>
                )}

              </div>

              <input
                type="file"
                id="signatureInput"
                hidden
                accept="image/*"
                onChange={handleFileUpload}
                disabled={!isEditMode}
              />

            </div>

          </div>

        </div>

      </div>

    </div>
  );
};

export default Settings;